import { useState, memo, useEffect, useCallback } from 'react'
import classnames from 'classnames'
import Zoomable from '../image/zoomable'
import CloseX from '../svg/close-x'
import Info from '../svg/info'
import { Desktop } from '../media'
import Modal from '../modal'

const SlideshowModal = memo(({ initialIndex, loop, images, onClose }) => {
  const [index, setIndex] = useState(initialIndex ?? 0)

  const showPrev = useCallback(() => {
    setIndex((current) => {
      if (loop) return current === 0 ? images.length - 1 : current - 1
      return Math.max(current - 1, 0)
    })
  }, [loop, images.length])

  const showNext = useCallback(() => {
    setIndex((current) => {
      if (loop) return current === images.length - 1 ? 0 : current + 1
      return Math.min(current + 1, images.length - 1)
    })
  }, [loop, images.length])

  useEffect(() => {
    const listener = (event) => {
      if (event.key === 'ArrowLeft') showPrev()
      if (event.key === 'ArrowRight') showNext()
    }

    document.addEventListener('keyup', listener)

    return () => document.removeEventListener('keyup', listener)
  }, [showPrev, showNext])

  return (
    <Modal onClose={() => onClose(index)}>
      <div className='container'>
        <div className='imagesContainer flex alignCenter justifyCenter'>
          <div className='overlay' onClick={onClose} />
          <Desktop>
            <button className='flatBtn close' onClick={onClose} aria-label='close'>
              <CloseX />
            </button>
          </Desktop>

          {images.map((entry, i) => (
            <div
              key={entry.id}
              className={classnames('image', { active: index === i })}
            >
              <Zoomable
                src={entry.url}
                alt='car'
                width='1440'
                height='810'
                fullRes
              />
            </div>
          ))}
        </div>
        <div className='controls'>
          <button
            aria-label='previous image'
            disabled={index === 0 && !loop}
            className='flatBtn indexChange prev'
            onClick={showPrev}
          ></button>
          <button
            aria-label='next image'
            disabled={index === images.length - 1 && !loop}
            className='flatBtn indexChange next'
            onClick={showNext}
          ></button>
        </div>
        <div className='info flex alignCenter'>
          <Info /> <span>use keyboard arrows or buttons to change image.</span>
        </div>
      </div>

      <style jsx>{`
        .container {
          position: relative;
          width: 100vw;
          height: 100vh;

          .imagesContainer {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            max-width: 87vw;
            width: 100%;
            height: 100%;
            max-height: 80vh;
            border-radius: 1rem;

            :global(.image) {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              max-width: 87vw;
              max-height: 80vh;
              object-fit: contain;
              transition: opacity 0.1s;
              opacity: 0;
            }

            :global(.image.active) {
              opacity: 1;
              z-index: 10;
              aspect-ratio: auto 1440 / 810;
              transform: translate(-50%, -50%) scale(1.5);
            }

            :global(.image.active > div:first-of-type) {
              height: 100%;
            }

            :global(.image.active > div:first-of-type > img) {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
          }

          .overlay {
            position: absolute;
            width: 100vw;
            height: 100vh;
            z-index: 5;
          }

          .controls {
            z-index: 7;

            .indexChange {
              position: absolute;
              z-index: 10;
              top: 50%;
              transform: translateY(-50%);
              width: 10rem;
              height: 10rem;
              opacity: 0.5;
              transition: opacity 0.2s;
              outline: none;

              &.prev {
                left: 0;

                &::before {
                  top: 31%;
                  left: 20%;
                  transform: rotate(-45deg);
                }

                &::after {
                  bottom: 31%;
                  left: 20%;
                  transform: rotate(45deg);
                }
              }
              &.next {
                right: 0;

                &::before {
                  top: 31%;
                  right: 20%;
                  transform: rotate(45deg);
                }

                &::after {
                  bottom: 31%;
                  right: 20%;
                  transform: rotate(-45deg);
                }
              }

              &::before,
              &::after {
                content: '';
                position: absolute;
                border-radius: 1rem;
                width: 5rem;
                height: 0.5rem;
                background: #fff;
              }

              &:hover,
              &:focus {
                opacity: 1;
              }

              &:focus {
                &::before,
                &::after {
                  background: var(--primary-color);
                }
              }

              &:disabled {
                opacity: 0.1;
              }
            }
          }

          .info {
            position: absolute;
            left: 50%;
            bottom: 2.5rem;
            padding: 1rem 2rem;
            border-radius: 1rem;
            background: rgba(0, 0, 0, 0.7);
            transform: translate(-50%, 0);
            color: #fff;
            font-size: 1.6rem;

            :global(svg) {
              max-width: 3rem;
              max-height: 3rem;
              fill: #fff;
            }

            span {
              margin: 0 0 0 1.5rem;
            }
          }
        }

        .close {
          position: absolute;
          right: -3rem;
          top: -3rem;
          width: 2.8rem;
          height: 2.8rem;
          background: #868786;
          border-radius: 50%;
          z-index: 6;

          :global(svg) {
            width: 100%;
            height: 100%;
            fill: #fff;
          }
        }
      `}</style>
    </Modal>
  )
})

export default SlideshowModal
